import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getPortfolioData } from '../services/api';
import { defaultLayoutKey } from '../layouts/layoutRegistry';
import { defaultThemeKey, getThemePreset } from '../theme/themePresets';

const PortfolioDataContext = createContext(null);

const defaultSiteConfig = {
  siteId: 'local-default',
  siteName: 'Portfolio',
  themeKey: defaultThemeKey,
  layoutKey: defaultLayoutKey,
  brandMark: 'PA.',
  browserTitle: '',
  metaDescription: '',
  callToActionLabel: 'Get In Touch',
  sectionOrder: ['hero', 'about', 'projects', 'skills', 'experience', 'contact'],
  sectionVisibility: {
    hero: true,
    about: true,
    projects: true,
    skills: true,
    experience: true,
    contact: true,
    footer: true,
  },
};

const PORTFOLIO_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

const getPortfolioCacheKey = () => `portfolioData:${window.location.host}`;

const normalizePortfolioPayload = (response) => ({
  siteConfig: {
    ...defaultSiteConfig,
    ...(response?.siteConfig ?? {}),
  },
  profile: response?.profile ?? null,
  projects: Array.isArray(response?.projects) ? response.projects : [],
  skills: {
    technical: Array.isArray(response?.skills?.technical) ? response.skills.technical : [],
    soft: Array.isArray(response?.skills?.soft) ? response.skills.soft : [],
  },
  experience: Array.isArray(response?.experience) ? response.experience : [],
  education: Array.isArray(response?.education) ? response.education : [],
});

const readCachedPortfolioData = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(getPortfolioCacheKey()) || 'null');
    if (!cached?.payload || !cached?.cachedAt || Date.now() - cached.cachedAt > PORTFOLIO_CACHE_TTL_MS) {
      return null;
    }
    return normalizePortfolioPayload(cached.payload);
  } catch {
    return null;
  }
};

const writeCachedPortfolioData = (payload) => {
  try {
    localStorage.setItem(getPortfolioCacheKey(), JSON.stringify({
      cachedAt: Date.now(),
      payload,
    }));
  } catch {
    // Storage can be blocked in private mode; the live API response still drives the UI.
  }
};

export const PortfolioDataProvider = ({ children }) => {
  const [initialState] = useState(() => {
    const cachedData = readCachedPortfolioData();
    return {
      data: cachedData || {
        siteConfig: defaultSiteConfig,
        profile: null,
        projects: [],
        skills: { technical: [], soft: [] },
        experience: [],
        education: [],
      },
      loading: !cachedData,
    };
  });
  const [data, setData] = useState(initialState.data);
  const [loading, setLoading] = useState(initialState.loading);
  const [error, setError] = useState(null);

  const getRuntimeOverrides = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      themeKey: params.get('theme') || localStorage.getItem('portfolioThemeOverride') || '',
      layoutKey: params.get('layout') || localStorage.getItem('portfolioLayoutOverride') || '',
    };
  };

  useEffect(() => {
    const runtimeOverrides = getRuntimeOverrides();
    const effectiveThemeKey = runtimeOverrides.themeKey || data.siteConfig?.themeKey || defaultThemeKey;
    const effectiveLayoutKey = runtimeOverrides.layoutKey || data.siteConfig?.layoutKey || defaultLayoutKey;
    const themePreset = getThemePreset(effectiveThemeKey);

    document.documentElement.dataset.theme = themePreset.key;
    document.documentElement.dataset.layout = effectiveLayoutKey;

    Object.entries(themePreset.variables).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }, [data.siteConfig]);

  useEffect(() => {
    const profileName = data.profile?.name?.trim();
    const profileTitle = data.profile?.title?.trim();
    const profileTagline = data.profile?.tagline?.trim();
    const profileAbout = data.profile?.about?.trim();

    const computedTitle = data.siteConfig?.browserTitle?.trim()
      || [profileName, profileTitle].filter(Boolean).join(' | ')
      || data.siteConfig?.siteName
      || 'Portfolio Website';

    const computedDescription = data.siteConfig?.metaDescription?.trim()
      || profileTagline
      || profileAbout
      || 'A customizable portfolio website for showcasing experience, projects, and services.';

    document.title = computedTitle;

    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute('content', computedDescription);
    }
  }, [data.profile, data.siteConfig]);

  useEffect(() => {
    let isMounted = true;

    const fetchPortfolioData = async () => {
      try {
        const response = await getPortfolioData();
        if (!isMounted) return;

        writeCachedPortfolioData(response);
        setData(normalizePortfolioPayload(response));
        setError(null);
      } catch (fetchError) {
        if (!isMounted) return;
        setError(fetchError);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPortfolioData();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(() => ({
    ...data,
    themeKey: getRuntimeOverrides().themeKey || data.siteConfig?.themeKey || defaultThemeKey,
    layoutKey: getRuntimeOverrides().layoutKey || data.siteConfig?.layoutKey || defaultLayoutKey,
    loading,
    error,
  }), [data, loading, error]);

  return (
    <PortfolioDataContext.Provider value={value}>
      {children}
    </PortfolioDataContext.Provider>
  );
};

export const usePortfolioData = () => {
  const context = useContext(PortfolioDataContext);

  if (!context) {
    throw new Error('usePortfolioData must be used within PortfolioDataProvider');
  }

  return context;
};
